export type ProductStatus = 'available' | 'planned' | 'coming-soon';
export type ProductDivision = 'Animal Nutrition' | 'Edible Oils';

export interface Product {
  id: string;
  name: string;
  slug: string;
  division: ProductDivision;
  category: string;
  animalType?: string;
  feedType?: string;
  status: ProductStatus;
  shortDescription: string;
  suitableFor: string[];
  keyBenefits: string[];
  packSizes: string[];
  enquiryCTA: string;
  disclaimer: string;
  seoTitle: string;
  seoDescription: string;
  image: string;
}

export const products: Product[] = [
  {
    id: 'cattle-feed',
    name: 'Cattle Feed',
    slug: 'cattle-feed',
    division: 'Animal Nutrition',
    category: 'Cattle Feed',
    animalType: 'Dairy cattle and buffalo',
    feedType: 'Balanced farm feed',
    status: 'available',
    shortDescription: 'Balanced cattle feed positioned for daily dairy farm nutrition, digestion support, and animal condition support.',
    suitableFor: ['Dairy cows', 'Dairy buffalo', 'Routine farm feeding', 'Dealer supply'],
    keyBenefits: ['Supports daily energy needs', 'Supports digestion and mineral balance', 'Designed for practical Indian dairy routines', 'Suitable for dealer and bulk supply'],
    packSizes: ['Standard bags', 'Bulk enquiry available'],
    enquiryCTA: 'Request cattle feed details',
    disclaimer: 'Feed selection should consider animal stage, roughage, water access, health, and local farm conditions.',
    seoTitle: 'Cattle Feed | Animal Nutrition for Indian Dairy Farms',
    seoDescription: 'Cattle feed support for practical daily nutrition on Indian dairy farms, with quality-focused manufacturing and dealer-ready supply.',
    image: '/images/products/cattle-feed/cattle-feed.webp'
  },
  {
    id: 'pig-feed',
    name: 'Pig Feed',
    slug: 'pig-feed',
    division: 'Animal Nutrition',
    category: 'Pig Feed',
    animalType: 'Pigs and swine farms',
    feedType: 'Stage-wise swine feed',
    status: 'available',
    shortDescription: 'Stage-wise pig feed positioning for growth, breeder routines, and practical swine farm management.',
    suitableFor: ['Swine farms', 'Grower routines', 'Breeder nutrition planning', 'Dealer supply'],
    keyBenefits: ['Stage-wise nutrition support', 'Supports consistent feeding routines', 'Built for farm and dealer conversations', 'Clear product guidance'],
    packSizes: ['Standard bags', 'Bulk enquiry available'],
    enquiryCTA: 'Request pig feed details',
    disclaimer: 'Use product guidance with farm-stage requirements and professional livestock advice where needed.',
    seoTitle: 'Pig Feed | Stage-wise Swine Nutrition Support',
    seoDescription: 'Pig feed positioning for stage-wise swine farm nutrition, dealer supply, and clear product information.',
    image: '/images/products/pig-feed/zfast-pig-feed.webp'
  },
  {
    id: 'pig-starter', name: 'Pig Starter Feed', slug: 'pig-starter', division: 'Animal Nutrition', category: 'Pig Feed',
    animalType: 'Young pigs', feedType: 'Starter swine feed', status: 'available',
    shortDescription: 'Stage-specific complete feed for young pigs, developed to support early growth, digestion, immunity, and feed conversion.',
    suitableFor: ['Young pigs after weaning', 'Starter-stage swine', 'Commercial pig farms'],
    keyBenefits: ['Supports fast, uniform growth', 'Supports gut health and immunity', 'Encourages efficient feed conversion'],
    packSizes: ['25 kg'], enquiryCTA: 'View Pig Starter',
    disclaimer: 'Follow the product label and adapt feeding to animal age, weight, health, and professional advice.',
    seoTitle: 'Pig Starter Feed | ZFAST Swine Nutrition',
    seoDescription: 'ZFAST Pig Starter is stage-specific swine feed for early growth, digestion, immunity, and efficient feed conversion.',
    image: '/images/products/pig-feed/zfast-starter.webp'
  },
  {
    id: 'pig-grower', name: 'Pig Grower Feed', slug: 'pig-grower', division: 'Animal Nutrition', category: 'Pig Feed',
    animalType: 'Growing pigs', feedType: 'Grower swine feed', status: 'available',
    shortDescription: 'Complete grower feed formulated to support steady body growth, condition, digestion, immunity, and feed efficiency.',
    suitableFor: ['Growing pigs', 'Commercial swine farms', 'Stage-wise feeding programmes'],
    keyBenefits: ['Supports optimal growth', 'Supports healthy digestion', 'Promotes body condition and immunity'],
    packSizes: ['25 kg'], enquiryCTA: 'View Pig Grower',
    disclaimer: 'Follow the product label and adapt feeding to animal age, weight, health, and professional advice.',
    seoTitle: 'Pig Grower Feed | ZFAST Swine Nutrition',
    seoDescription: 'ZFAST Pig Grower supports steady swine growth, body condition, digestion, immunity, and feed efficiency.',
    image: '/images/products/pig-feed/zfast-grower.webp'
  },
  {
    id: 'pig-finisher', name: 'Pig Finisher Feed', slug: 'pig-finisher', division: 'Animal Nutrition', category: 'Pig Feed',
    animalType: 'Finishing pigs', feedType: 'Finisher swine feed', status: 'available',
    shortDescription: 'Finisher-stage complete feed designed to support weight gain, meat quality, gut health, immunity, and farm returns.',
    suitableFor: ['Finishing pigs', 'Market-weight programmes', 'Commercial swine farms'],
    keyBenefits: ['Supports maximum weight gain', 'Supports meat quality', 'Encourages better feed conversion'],
    packSizes: ['25 kg'], enquiryCTA: 'View Pig Finisher',
    disclaimer: 'Follow the product label and adapt feeding to animal age, weight, health, and professional advice.',
    seoTitle: 'Pig Finisher Feed | ZFAST Swine Nutrition',
    seoDescription: 'ZFAST Pig Finisher supports weight gain, meat quality, gut health, immunity, and feed conversion.',
    image: '/images/products/pig-feed/zfast-finisher.webp'
  },
  {
    id: 'sow-gestation', name: 'Sow Gestation Feed', slug: 'sow-gestation', division: 'Animal Nutrition', category: 'Pig Feed',
    animalType: 'Gestating sows', feedType: 'Breeder swine feed', status: 'available',
    shortDescription: 'Gestation-stage sow feed developed to support body condition, foetal development, immunity, and reproductive performance.',
    suitableFor: ['Gestating sows', 'Breeder farms', 'Reproductive feeding programmes'],
    keyBenefits: ['Supports optimal body condition', 'Supports foetal development', 'Supports mammary and reproductive health'],
    packSizes: ['50 kg'], enquiryCTA: 'View Sow Gestation',
    disclaimer: 'Follow the product label and seek professional advice for breeder-stage feeding programmes.',
    seoTitle: 'Sow Gestation Feed | ZFAST Breeder Nutrition',
    seoDescription: 'ZFAST Sow Gestation Feed supports sow body condition, foetal development, immunity, and reproductive performance.',
    image: '/images/products/pig-feed/sow-gestation.webp'
  },
  {
    id: 'sow-lactation', name: 'Sow Lactation Feed', slug: 'sow-lactation', division: 'Animal Nutrition', category: 'Pig Feed',
    animalType: 'Lactating sows', feedType: 'Breeder swine feed', status: 'available',
    shortDescription: 'Lactation-stage sow feed designed to support milk production, immunity, body condition, and healthy litters.',
    suitableFor: ['Lactating sows', 'Breeder farms', 'Sow and litter support programmes'],
    keyBenefits: ['Supports milk yield and quality', 'Supports strong immunity', 'Helps maintain sow body condition'],
    packSizes: ['50 kg'], enquiryCTA: 'View Sow Lactation',
    disclaimer: 'Follow the product label and seek professional advice for breeder-stage feeding programmes.',
    seoTitle: 'Sow Lactation Feed | ZFAST Breeder Nutrition',
    seoDescription: 'ZFAST Sow Lactation Feed supports milk production, immunity, sow body condition, and healthy litters.',
    image: '/images/products/pig-feed/sow-lactation.webp'
  },
  {
    id: 'mustard-oil',
    name: 'Mustard Oil',
    slug: 'mustard-oil',
    division: 'Edible Oils',
    category: 'Mustard Oil',
    status: 'coming-soon',
    shortDescription: 'Traditional cooking oil with strong flavour and origin-led positioning for homes, retailers, and bulk buyers.',
    suitableFor: ['Household kitchens', 'Retail counters', 'Traditional cooking', 'Bulk buyers'],
    keyBenefits: ['Strong regional flavour positioning', 'Useful for everyday Indian cooking', 'Suitable for dealer and retail conversations', 'Retail and bulk supply enquiries'],
    packSizes: ['Retail packs', 'Bulk enquiry available'],
    enquiryCTA: 'Request mustard oil details',
    disclaimer: 'Final supply details depend on pack size, buyer category, location, and confirmed availability.',
    seoTitle: 'Z-Strong Mustard Oil | Zfast Agritech India',
    seoDescription: 'Z-strong mustard oil enquiries for traditional Indian cooking, retail interest, and bulk edible oil supply.',
    image: '/images/products/edible-oils/oil-mustard.webp'
  },
  {
    id: 'soybean-oil',
    name: 'Soybean Refined Oil',
    slug: 'soybean-oil',
    division: 'Edible Oils',
    category: 'Soybean Refined Oil',
    status: 'coming-soon',
    shortDescription: 'Neutral edible oil positioning for restaurants, food service buyers, retail counters, and regular cooking needs.',
    suitableFor: ['Restaurants', 'Food service', 'Retail packs', 'Bulk buyers'],
    keyBenefits: ['Neutral cooking profile', 'Suitable for institutional enquiries', 'Useful for retail and food service buyers', 'Bulk supply enquiry support'],
    packSizes: ['Retail packs', 'Bulk enquiry available'],
    enquiryCTA: 'Request soybean oil details',
    disclaimer: 'Final supply details depend on pack size, buyer category, location, and confirmed availability.',
    seoTitle: 'Soya Oil for Cooking | Soybean Refined Oil | Zfast',
    seoDescription: 'Premium soya oil for cooking and soybean refined oil enquiries for food service, institutional kitchens, retail packs, and bulk buyers.',
    image: '/images/products/edible-oils/oil-soybean.webp'
  },
  {
    id: 'sunflower-oil',
    name: 'Sunflower Oil',
    slug: 'sunflower-oil',
    division: 'Edible Oils',
    category: 'Sunflower Oil',
    status: 'coming-soon',
    shortDescription: 'Light everyday oil positioning for family kitchens, retail packs, and versatile cooking use.',
    suitableFor: ['Family kitchens', 'Retail packs', 'General cooking', 'Dealer supply'],
    keyBenefits: ['Light cooking positioning', 'Clear retail product story', 'Dealer-ready enquiry flow', 'Retail and bulk supply enquiries'],
    packSizes: ['Retail packs', 'Bulk enquiry available'],
    enquiryCTA: 'Request sunflower oil details',
    disclaimer: 'Final supply details depend on pack size, buyer category, location, and confirmed availability.',
    seoTitle: 'Sunflower Oil for Deep Frying Suppliers | Zfast Agritech',
    seoDescription: 'Zfast sunflower oil for deep frying suppliers, everyday cooking, retail interest, and bulk edible oil requirements.',
    image: '/images/products/edible-oils/oil-sunflower.webp'
  }
];

export const activeProducts = products.filter((product) => product.status === 'available');
export const oilProducts = products.filter((product) => product.division === 'Edible Oils');
export const animalNutritionProducts = products.filter((product) => product.division === 'Animal Nutrition');
export const getProductBySlug = (slug: string) => products.find((product) => product.slug === slug);
